import { useState, useEffect, useCallback } from 'react';

// ── INSTALLING TUNIFY AS AN APP ──────────────────────────────────────────────
//
// Everything the UI needs to offer "Install app", and nothing about how it
// looks — see InstallAppButton for that.
//
// THREE DIFFERENT WORLDS, and the hook has to tell them apart:
//
//   Chrome, Edge, Android    fire `beforeinstallprompt`. Capture the event and
//                            it can be replayed later from a click of ours.
//   iOS Safari               never fires it and has no install API at all. The
//                            only route is Share → Add to Home Screen, so the
//                            honest thing is to say so.
//   Already installed        neither, and offering to install again would be
//                            nonsense.
//
// THE EVENT MUST BE CAPTURED AT LOAD, not when the button is rendered. The
// browser fires it once, early, and if nothing calls preventDefault() the
// chance is gone — which is why this listener is registered on mount and the
// event is kept in state rather than being asked for on demand.

// ── THE LISTENER LIVES AT MODULE SCOPE, NOT IN THE COMPONENT ────────────────
//
// This was originally a plain useEffect, and it never caught the event once.
// The install row lives inside the user dropdown, so the component only mounts
// when someone OPENS that menu — by which time `beforeinstallprompt` fired long
// ago and will not fire again. The offer was therefore invisible in every
// browser that supports it, while the iOS path (which needs no event) worked
// fine and hid the problem.
//
// Registering on import fixes it: this module is pulled in by the header, which
// is on every page, so the listener is in place before the browser gets to the
// event. Components then subscribe to what was captured.
let deferredEvent = null;
let didInstall = false;
const subscribers = new Set();

const notify = () => subscribers.forEach(fn => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();            // suppress the browser's own mini-infobar
    deferredEvent = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    didInstall = true;
    deferredEvent = null;
    notify();
  });
}

// The browser's own install UI is suppressed in favour of ours, so the offer
// appears where it makes sense in the app rather than as a mini-infobar.
export function usePwaInstall() {
  const [, force] = useState(0);
  const deferred = deferredEvent;
  const installed = didInstall;

  // Re-render this component whenever the captured event changes.
  useEffect(() => {
    const fn = () => force(n => n + 1);
    subscribers.add(fn);
    return () => { subscribers.delete(fn); };
  }, []);

  const setDeferred = useCallback((v) => { deferredEvent = v; notify(); }, []);

  // Running from the home screen / as an installed app.
  //
  // Two checks because the platforms disagree: everything modern reports the
  // display-mode media query, and iOS Safari instead sets navigator.standalone.
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.matchMedia?.('(display-mode: fullscreen)').matches
    || window.navigator.standalone === true
  );

  // iOS — including Chrome and Firefox there, which are Safari underneath and
  // have no install API either. iPadOS reports itself as a Mac, so the touch
  // check is what catches an iPad.
  const isIOS = typeof navigator !== 'undefined' && (
    /iphone|ipad|ipod/i.test(navigator.userAgent)
    || (/mac/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  );

  /**
   * Show the browser's install dialog.
   * @returns 'accepted' | 'dismissed' | 'unavailable'
   */
  const install = useCallback(async () => {
    if (!deferred) return 'unavailable';
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      // The event is single-use: once prompted it cannot be replayed, so it is
      // dropped either way. If they dismissed, the browser will fire a fresh
      // one on a later visit.
      setDeferred(null);
      return outcome;                       // 'accepted' | 'dismissed'
    } catch {
      setDeferred(null);
      return 'dismissed';
    }
  }, [deferred]);

  return {
    // Can we show the real install dialog?
    canInstall: !!deferred && !isStandalone,
    // Can we only explain how it is done by hand?
    needsIOSHelp: isIOS && !isStandalone,
    isStandalone,
    installed,
    install,
  };
}
